<template>
  <div ref="elementRef">
    <div v-style:grid-cols="cols" class="grid relative">
      <cms-base-caption
        v-if="captionText && captionPosition !== 'off'"
        :class="{ 'pt-14 md:pt-20': firstElement && header.transparent }"
        v-bind="{ captionColor, captionStyle, captionPosition, captionText }"
      />
      <cms-base-responsive-media
        v-for="(media, key) in mediaList"
        :key
        :class-name="['full', key ? {
          'hidden': responsiveLayout.sm.layout === 'below-1media',
          'md:hidden': responsiveLayout.md?.layout === 'below-1media',
        } : '']"
        :lazy
        :media
      />
      <cms-base-gradient
        v-if="gradientStyle && gradientStyle !== 'off'"
        v-bind="{ gradientTone, gradientStyle, gradientBreakpoints }"
        position="bottom"
      />
    </div>
    <div v-style:bg="backgroundColor" class="p-4 lg:px-10 lg:py-8" :class="alignment">
      <cms-base-eyebrow
        class="mb-2"
        v-bind="{
          eyebrowLogoResponsive,
          eyebrowText,
          eyebrowTextColor,
        }"
      />
      <h1 v-if="title" v-style:c="titleColor" :class="titleStyle">
        <base-link
          v-if="mediaTarget"
          :target="mediaTarget.targetAttribute"
          :to="mediaTarget.url"
          @click="handlePromotionClick('title')"
        >
          <span v-if="showTitleHtml" v-html="titleHtml" />
          <span v-else>{{ title }}</span>
        </base-link>
        <template v-else>
          <span v-if="showTitleHtml" v-html="titleHtml" />
          <span v-else>{{ title }}</span>
        </template>
      </h1>
      <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle">
        <span v-if="showTitleHtml" v-html="subtitleHtml" />
        <span v-else>{{ subtitle }}</span>
      </p>
      <cms-rich-text
        v-if="richText"
        v-style:c="richTextColor"
        class="mt-2 pointer-within"
        :content="{ richText }"
      />
      <cms-shared-buttons
        v-if="linkType === 'CTA' && targets.length"
        class="mt-6"
        :class="alignment"
        :equal-target-size="equalTargetSize"
        :targets
        :text-link-style="getTextLinkStyles(content)"
        @click="handlePromotionClick(`cta${$event + 1}`)"
      />
      <cms-rich-text
        v-if="richTextBelowTargets"
        v-style:c="richTextBelowTargetsColor"
        class="mt-2 pointer-within"
        :content="{ richText: richTextBelowTargets }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SectionProvider } from '#content/contexts/section'
import type { Responsive } from '#types/common'
import type { HeroBlockBelow } from '#types/components/cms/hero'
import type { Target } from '#types/target'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{
  content: HeroBlockBelow
  firstElement: boolean
}>()

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const { showTitleHtml } = useFeatureFlags()

const {
  subtitleColor,
  backgroundColor,
  captionColor = subtitleColor,
  captionPosition,
  captionStyle,
  captionText,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowText,
  eyebrowTextColor = subtitleColor,
  gradientBreakpoints,
  gradientStyle,
  gradientTone,
  linkType,
  mediaList,
  mediaTarget,
  name,
  promotionTracking,
  responsiveLayout,
  richText,
  richTextBelowTargets,
  richTextBelowTargetsColor = subtitleColor,
  richTextColor = subtitleColor,
  subtitle,
  subtitleHtml,
  subtitleStyle,
  targets = [] as Target[],
  title,
  titleColor,
  titleHtml,
  titleStyle,
} = content

const header = useHeaderStore()
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const responsiveEntries = Object.entries(responsiveLayout)

const cols = responsiveEntries.reduce((acc, [br, value]) => ({
  ...acc,
  [br]: value.layout === 'below-2media' ? 'repeat(2, 1fr)' : '1fr'
}), {} as Responsive)

const alignmentMap = {
  center: 'text-center justify-center',
  right: 'text-right justify-end',
}

const alignment = responsiveEntries.reduce(
  (acc, [br, { alignment }]) => ([...acc, `${br !== 'sm' ? `${br}:` : ''}${alignmentMap[alignment]}`]),
  [] as string[]
)
</script>
